import { Controller } from '@hotwired/stimulus';

/**
 * Image Upload Field Controller
 *
 * On file select, renders a client-side preview card (thumbnail + filename +
 * size · type) for the chosen file, before upload — mirroring the server's
 * attached-image card. While a new file is previewed, the server-rendered
 * "attached" card is hidden. HEIC/HEIF can't be rendered by <img> in most
 * browsers, so the thumbnail hides on error and the card keeps filename + type.
 *
 * Targets:
 * - input: the <input type="file">
 * - filename: legacy inline filename span beside the picker button (cleared)
 * - previewCard: client preview container (hidden until a file is chosen)
 * - previewImage: decorative <img> thumbnail (alt="")
 * - previewFilename / previewMeta: chosen file's name and "size · EXT"
 * - attachedCard: the server-rendered current-image card, if any
 */
export default class extends Controller {
  static targets = [
    'input',
    'filename',
    'previewCard',
    'previewImage',
    'previewFilename',
    'previewMeta',
    'attachedCard',
  ];

  disconnect() {
    this.#revoke();
  }

  fileSelected() {
    const file = this.inputTarget.files[0];

    if (!file) {
      this.#hidePreview();
      return;
    }

    if (this.hasPreviewFilenameTarget) this.previewFilenameTarget.textContent = file.name;
    if (this.hasPreviewMetaTarget) this.previewMetaTarget.textContent = this.#meta(file);

    if (this.hasPreviewImageTarget) {
      this.#revoke();
      this.objectUrl = URL.createObjectURL(file);
      this.previewImageTarget.classList.remove('hidden');
      // HEIC/HEIF (and any unrenderable type): drop the broken thumbnail,
      // keep the filename + type text.
      this.previewImageTarget.onerror = () => this.previewImageTarget.classList.add('hidden');
      this.previewImageTarget.src = this.objectUrl;
    }

    if (this.hasPreviewCardTarget) this.previewCardTarget.classList.remove('hidden');
    if (this.hasAttachedCardTarget) this.attachedCardTarget.classList.add('hidden');
    // The card now shows the filename; clear the redundant inline span.
    if (this.hasFilenameTarget) this.filenameTarget.textContent = '';
  }

  #hidePreview() {
    if (this.hasPreviewCardTarget) this.previewCardTarget.classList.add('hidden');
    if (this.hasAttachedCardTarget) this.attachedCardTarget.classList.remove('hidden');
    this.#revoke();
  }

  #revoke() {
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = null;
    }
  }

  #meta(file) {
    const ext = (file.name.split('.').pop() || '').toUpperCase();
    return `${this.#formatSize(file.size)} · ${ext}`;
  }

  #formatSize(bytes) {
    if (bytes < 1024) return `${bytes} o`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} Ko`;
    return `${(bytes / 1048576).toFixed(1)} Mo`;
  }
}
